import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { environment } from '../../../environments/environment';
import { APP_VERSION, BUILD_VERSION } from '../../../environments/version';

const SERVICE_WORKER_URL = `/service-worker.js?v=${encodeURIComponent(
  BUILD_VERSION,
)}`;
const RELOAD_STORAGE_KEY = `prime-solutions-sw-reloaded-${BUILD_VERSION}`;

export type CacheStatus =
  | 'idle'
  | 'registering'
  | 'checking'
  | 'ready'
  | 'updated'
  | 'unsupported'
  | 'error';

export interface AppVersionState {
  appVersion: string;
  buildVersion: string;
  workerVersion: string;
  status: CacheStatus;
  message: string;
}

interface ServiceWorkerMessage {
  type?: string;
  version?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AppVersionService {
  private readonly stateSubject = new BehaviorSubject<AppVersionState>({
    appVersion: APP_VERSION,
    buildVersion: BUILD_VERSION,
    workerVersion: BUILD_VERSION,
    status: 'idle',
    message: 'Cache pendiente',
  });

  readonly state$ = this.stateSubject.asObservable();

  private initialized = false;
  private reloadScheduled = false;
  private registration?: ServiceWorkerRegistration;

  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    if (!environment.production) {
      this.setState({
        status: 'unsupported',
        message: 'Cache desactivado',
      });
      return;
    }

    if (!this.canUseServiceWorker()) {
      this.setState({
        status: 'unsupported',
        message: 'Cache no disponible',
      });
      return;
    }

    this.bindClientMessages();
    this.setState({
      status: 'registering',
      message: 'Preparando cache',
    });

    const register = (): void => {
      void this.registerServiceWorker();
    };

    if (document.readyState === 'complete') {
      register();
      return;
    }

    window.addEventListener('load', register, { once: true });
  }

  checkForUpdates(): void {
    const registration = this.registration;

    if (!registration) {
      this.initialize();
      return;
    }

    this.setState({
      status: 'checking',
      message: 'Buscando version',
    });

    registration
      .update()
      .then(() => {
        this.requestWorkerVersion(registration);
        this.setState({
          status: 'ready',
          message: 'Cache al dia',
        });
      })
      .catch(() => {
        this.setState({
          status: 'error',
          message: 'No se pudo verificar',
        });
      });
  }

  private async registerServiceWorker(): Promise<void> {
    try {
      const registration = await navigator.serviceWorker.register(
        SERVICE_WORKER_URL,
        {
          scope: '/',
          updateViaCache: 'none',
        },
      );

      this.registration = registration;
      this.bindRegistration(registration);
      window.addEventListener('focus', () => void registration.update());
      this.requestWorkerVersion(registration);
      this.setState({
        status: 'ready',
        message: 'Cache activo',
      });

      registration.update().catch(() => undefined);
    } catch {
      this.setState({
        status: 'error',
        message: 'Cache no disponible',
      });
    }
  }

  private bindClientMessages(): void {
    navigator.serviceWorker.addEventListener(
      'message',
      (event: MessageEvent) => {
        const data = event.data as ServiceWorkerMessage | undefined;

        if (data?.type === 'SW_VERSION' && data.version) {
          this.setState({
            workerVersion: data.version,
            status: 'ready',
            message: 'Cache activo',
          });
          this.reloadIfNewVersion(data.version);
        }

        if (data?.type === 'SW_ACTIVATED') {
          const workerVersion = data.version || BUILD_VERSION;

          console.info(`[ServiceWorker] Active version: ${workerVersion}`);

          this.setState({
            workerVersion,
            status: 'updated',
            message: 'Cache actualizado',
          });
          this.reloadIfNewVersion(workerVersion);
        }
      },
    );
  }

  private bindRegistration(registration: ServiceWorkerRegistration): void {
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }

    registration.addEventListener('updatefound', () => {
      const installingWorker = registration.installing;

      this.setState({
        status: 'checking',
        message: 'Actualizando cache',
      });

      installingWorker?.addEventListener('statechange', () => {
        if (installingWorker.state === 'activated') {
          this.requestWorkerVersion(registration);
          this.setState({
            status: 'updated',
            message: 'Cache actualizado',
          });
        }

        if (installingWorker.state === 'redundant') {
          this.setState({
            status: 'error',
            message: 'Cache interrumpido',
          });
        }
      });
    });
  }

  private requestWorkerVersion(registration: ServiceWorkerRegistration): void {
    const worker = registration.active || navigator.serviceWorker.controller;
    worker?.postMessage({ type: 'GET_VERSION' });
  }

  private canUseServiceWorker(): boolean {
    if (
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      typeof navigator === 'undefined' ||
      !this.hasServiceWorkerApi()
    ) {
      return false;
    }

    const hostname = window.location.hostname;
    const isLocalhost =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '[::1]';

    return window.isSecureContext || isLocalhost;
  }

  private hasServiceWorkerApi(): boolean {
    return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
  }

  private setState(partial: Partial<AppVersionState>): void {
    this.stateSubject.next({
      ...this.stateSubject.value,
      ...partial,
    });
  }

  private reloadIfNewVersion(workerVersion: string): void {
    if (
      this.reloadScheduled ||
      !workerVersion ||
      workerVersion === BUILD_VERSION ||
      this.wasReloadAlreadyRequested()
    ) {
      return;
    }

    this.reloadScheduled = true;
    window.setTimeout(() => this.reloadApplication(), 250);
  }

  private reloadApplication(): void {
    this.markReloadAsRequested();
    window.location.reload();
  }

  private wasReloadAlreadyRequested(): boolean {
    try {
      return sessionStorage.getItem(RELOAD_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private markReloadAsRequested(): void {
    try {
      sessionStorage.setItem(RELOAD_STORAGE_KEY, 'true');
    } catch {
      // Reload is still the correct fallback if storage is unavailable.
    }
  }
}
